import { cva } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import Imgix from 'react-imgix';

type Variants = 'base' | 'compact';

export const uspTextVariants = cva('', {
  variants: {
    variant: {
      base: 'text-cove-navy-blue mt-cove-15 lg:ml-cove-25 px-cove-15 xs:px-cove-25 sm:px-cove-50 text-center text-[12px] xs:text-[14px] font-medium lg:mt-0 lg:text-[20px] lg:uppercase"',
      compact:
        'text-cove-navy-blue text-base font-bold [&>br]:!hidden sm:[&>br]:!block lg:[&>br]:!hidden',
    },
  },
  defaultVariants: {
    variant: 'base',
  },
});

export const uspIconVariants = cva('', {
  variants: {
    variant: {
      base: 'flex',
      compact:
        'flex [&_svg]:!max-w-[42px] [&_svg]:!max-h-[42px] [&_img]:!max-w-[42px] [&_img]:!max-h-[42px]',
    },
  },
  defaultVariants: {
    variant: 'base',
  },
});

export const uspContainerVariants = cva('', {
  variants: {
    variant: {
      base: 'border-r-cove-baby-dove flex w-full max-w-[33.33%] flex-[0_0_33.33%] flex-col items-center justify-center border-r last:border-none lg:flex-row',
      compact:
        'flex !gap-cove-15 !items-center !flex-row sm:border-none sm:last-of-type:pb-0 last-of-type:pb-cove-25 last-of-type:border-b last-of-type:border-cove-baby-dove',
    },
  },
  defaultVariants: {
    variant: 'base',
  },
});

export const uspWrapperVariants = cva('', {
  variants: {
    variant: {
      base: 'my-cove-35 mx-auto flex w-full max-w-screen-xxl justify-center',
      compact:
        'w-full max-w-screen-xl flex-col sm:flex-row flex sm:items-center justify-around p-5 gap-cove-15 sm:gap-0 sm:px-0 sm:py-cove-35 sm:border-b-cove-baby-dove sm:border-b mx-auto',
    },
  },
  defaultVariants: {
    variant: 'base',
  },
});

const USPIcons = ({
  variant = 'base',
  ...props
}: HTMLAttributes<HTMLDivElement> & { variant?: Variants }) => {
  const { t } = useTranslation('components');

  const compactItems = [
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="42"
          height="42"
          viewBox="0 0 42 42"
          fill="none"
          className="w-cove-50 h-cove-50 lg:w-cove-75 lg:h-cove-75"
        >
          <rect width="42" height="42" rx="21" fill="#F2EFEB" />
          <g clipPath="url(#clip0_2582_535)">
            <path
              d="M20.5 19.906V27.2818L27.5764 24.6623V17.3686L20.5 19.906Z"
              stroke="#002334"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20.5 19.906V27.2818L13.4236 24.6623V17.3686L20.5 19.906Z"
              stroke="#002334"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M27.5764 17.3686L20.5 14.975L13.4236 17.3686L20.5 19.906L27.5764 17.3686Z"
              stroke="#002334"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16.7728 22.2276V18.5705L23.6654 16.0434"
              stroke="#002334"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M30.9563 11.5645C33.1518 14.0505 34.4894 17.3275 34.4894 20.9127C34.4894 28.6891 28.2299 34.9863 20.5 34.9863C17.4775 34.9863 14.6796 34.0207 12.4025 32.3873"
              stroke="#002334"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M30.3436 14.7182L30.9563 11.5645L34.3669 12.0781"
              stroke="#002334"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.0437 30.4355C7.8483 27.9495 6.51062 24.6725 6.51062 21.0873C6.51062 13.3108 12.7701 7.01361 20.5001 7.01361C23.5226 7.01361 26.3205 7.97925 28.5976 9.61262"
              stroke="#002334"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.6563 27.2818L10.0436 30.4355L6.63306 29.9219"
              stroke="#002334"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_2582_535">
              <rect
                width="29"
                height="29"
                fill="white"
                transform="translate(6 6.5)"
              />
            </clipPath>
          </defs>
        </svg>
      ),
      text: (
        <>
          {t('ui-core.usp.0.text0', { defaultValue: 'Try Risk-Free' })}
          <br className="block lg:hidden" />{' '}
          {t('ui-core.usp.0.text1', { defaultValue: 'For 60 Days' })}
        </>
      ),
    },
    {
      icon: (
        //@ts-expect-error - Imgix type error because of JSX runtime
        <Imgix
          src="https://cdn.shopify.com/s/files/1/0612/5086/3345/files/cs23-icon.png?v=1717611673"
          htmlAttributes={{ alt: 'DIY & Pro Install Options' }}
          className="w-cove-50 h-cove-50 lg:w-cove-75 lg:h-cove-75"
        />
      ),
      text: (
        <>
          {t('ui-core.usp.1.text0', { defaultValue: 'DIY & Pro' })}
          <br className="block lg:hidden" />{' '}
          {t('ui-core.usp.1.text1', { defaultValue: 'Install Options' })}
        </>
      ),
    },
    {
      icon: (
        <svg
          fill="none"
          viewBox="0 0 73 73"
          xmlns="http://www.w3.org/2000/svg"
          className="w-cove-50 h-cove-50 lg:w-cove-75 lg:h-cove-75"
        >
          <circle cx="36.4277" cy="36.4277" fill="#F9F9F9" r="36.4277"></circle>
          <path
            d="M50.6061 31.3874H22V51.5071H50.6061V31.3874Z"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
          <path
            d="M50.6061 31.3874L36.3098 22L22 31.3874"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
          <path
            d="M33.5387 38.0984H26.8276V51.5205H33.5387V38.0984Z"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
          <path
            d="M45.7772 38.0984H37.8691V44.8095H45.7772V38.0984Z"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
          <path
            d="M41.8232 25.6178V22H46.2749V28.3883"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
        </svg>
      ),
      text: (
        <>
          {t('ui-core.usp.2.text0', { defaultValue: '24/7' })}
          <br className="block lg:hidden" />{' '}
          {t('ui-core.usp.2.text1', { defaultValue: 'Support' })}
        </>
      ),
    },
  ];

  const items = [
    {
      icon: (
        <svg
          fill="none"
          viewBox="0 0 73 73"
          xmlns="http://www.w3.org/2000/svg"
          className="w-cove-50 h-cove-50 lg:w-cove-75 lg:h-cove-75"
        >
          <circle cx="36.4277" cy="36.4277" fill="#F9F9F9" r="36.4277"></circle>
          <path
            d="M37.7196 12.7503C36.7462 13.7237 35.9319 14.6035 35.9039 14.7064C35.8758 14.8188 35.9132 15.034 35.9881 15.1744C36.2221 15.6143 39.6384 18.9557 39.863 18.9557C40.2 18.9557 40.5463 18.6001 40.5463 18.2538C40.5463 17.973 40.4059 17.7858 39.3857 16.7281L38.2157 15.5301L38.8896 15.605C43.2699 16.1104 47.5848 18.207 50.7577 21.3799C56.9726 27.6041 58.4701 36.9826 54.4922 44.7886C52.8543 47.9896 50.5237 50.5822 47.4725 52.5758C46.3025 53.3433 44.5803 54.2044 43.3635 54.6256C42.8768 54.7941 42.3995 55.0094 42.3059 55.103C41.9409 55.468 42.231 56.2074 42.7364 56.2074C43.3916 56.2074 46.3119 54.9158 47.9124 53.933C49.0917 53.1936 50.3459 52.2108 51.441 51.1532C54.8947 47.8305 57.0755 43.6092 57.8243 38.7983C58.0583 37.3008 58.0583 34.0249 57.8243 32.5273C56.8602 26.3031 53.4252 20.94 48.2212 17.5518C46.2183 16.2508 43.1951 15.0153 40.9207 14.566C40.0221 14.3976 38.6275 14.1823 38.3655 14.1823C38.3093 14.1823 38.7773 13.6582 39.4044 13.0311C40.2936 12.1325 40.5463 11.8143 40.5463 11.6271C40.5463 11.2621 40.2374 11 39.8256 11C39.5073 11 39.3669 11.1123 37.7196 12.7503Z"
            fill="#002334"
          ></path>
          <path
            d="M29.1276 15.5394C25.5616 16.831 22.1734 19.2646 19.7398 22.2784C17.8679 24.5902 16.4265 27.4543 15.6871 30.281C15.0974 32.5366 14.8634 35.4662 15.0787 37.7687C16.0521 48.0082 23.933 56.0108 34.2099 57.1995L34.7622 57.2556L33.6296 58.4069C32.4503 59.5956 32.3474 59.7828 32.6375 60.2039C32.8153 60.4567 33.1897 60.5503 33.4799 60.4005C33.8449 60.2039 37.0834 56.9468 37.1395 56.7034C37.177 56.5724 37.1302 56.3852 37.0366 56.2354C36.7277 55.7768 33.4986 52.5571 33.2833 52.5009C33.0681 52.4447 32.6937 52.5945 32.572 52.7817C32.5346 52.8379 32.4971 53.025 32.4971 53.2029C32.4971 53.493 32.6282 53.6521 33.6764 54.7192C34.3316 55.3743 34.7809 55.8985 34.6873 55.8798C34.5937 55.861 34.107 55.7955 33.6203 55.7394C30.9621 55.4118 27.9296 54.2512 25.4399 52.6226C16.1176 46.4826 13.5343 33.95 19.6837 24.6745C22.0985 21.0336 25.7207 18.1788 29.6986 16.7936C30.6345 16.4567 30.906 16.2695 30.906 15.9232C30.906 15.5956 30.5597 15.2118 30.2695 15.2118C30.1291 15.2212 29.6143 15.3616 29.1276 15.5394Z"
            fill="#002334"
          ></path>
          <path
            d="M34.6029 20.0414C32.7591 20.3222 31.1586 20.7996 29.6423 21.5296C25.2151 23.673 22.1732 27.5947 21.0594 32.5741C20.8722 33.3977 20.8535 33.7815 20.8535 35.7096C20.8535 38.1338 20.9471 38.7889 21.5555 40.6702C23.0718 45.3127 26.8344 49.0846 31.4768 50.629C33.3768 51.2654 34.163 51.3777 36.5217 51.3777C38.8803 51.3777 39.6665 51.2654 41.5666 50.629C46.1809 49.094 49.9061 45.3688 51.441 40.7638C52.0775 38.8545 52.1898 38.0682 52.1898 35.7096C52.1898 33.8657 52.1617 33.482 51.9839 32.6209C50.5987 26.2563 45.9656 21.6045 39.6572 20.2567C38.9084 20.0976 38.3375 20.0508 36.8025 20.0321C35.7448 20.0134 34.762 20.0227 34.6029 20.0414ZM39.3389 21.5764C41.2483 21.9789 43.2794 22.8493 44.805 23.9257C45.8533 24.6651 47.5287 26.3312 48.2494 27.3514C49.4006 28.98 50.2711 31.0017 50.6736 33.0234C50.9543 34.4179 50.9731 36.7953 50.711 38.1899C49.7376 43.4314 46.0499 47.6994 41.0611 49.3654C39.4138 49.9083 38.4311 50.0674 36.5217 50.0674C35.0896 50.0674 34.6216 50.0299 33.8261 49.8615C32.5625 49.59 31.5049 49.2437 30.5502 48.8038C26.2821 46.8383 23.2964 43.0382 22.3698 38.3958C22.0703 36.8983 22.0703 34.5209 22.3698 33.0234C22.7723 31.0017 23.6427 28.98 24.794 27.3514C25.4866 26.3686 27.1807 24.6745 28.1634 23.9819C30.026 22.6622 32.2911 21.7543 34.5561 21.4267C35.7916 21.2488 38.1596 21.3237 39.3389 21.5764Z"
            fill="#002334"
          ></path>
          <path
            d="M36.2974 25.2173C36.1664 25.3577 36.1196 25.6292 36.0821 26.3311L36.026 27.2671L35.4082 27.4169C35.0713 27.5011 34.5565 27.6789 34.2757 27.8193C33.6393 28.1282 32.5348 29.2233 32.2259 29.841C30.9998 32.3588 32.2072 35.0357 34.9215 35.8406C35.2023 35.9248 35.5486 35.9904 35.6984 35.9904H35.9605V38.1244C35.9605 39.3037 35.9324 40.9136 35.8949 41.6998L35.8388 43.1225L35.4457 43.0757C34.8467 42.9914 34.0604 42.6638 33.5925 42.3082C33.0309 41.8776 32.6284 41.082 32.5442 40.221C32.4599 39.4909 32.3102 39.2663 31.8703 39.2663C31.4116 39.2663 31.187 39.5377 31.187 40.0806C31.187 40.6609 31.4116 41.5968 31.6644 42.0929C31.9358 42.6358 32.722 43.4313 33.2836 43.7496C33.7516 44.021 34.8092 44.3579 35.5206 44.4609L35.8669 44.5171V45.2471C35.8669 45.8181 35.9043 46.0333 36.0353 46.1737C36.2787 46.4732 36.625 46.52 36.9245 46.286C37.1585 46.0989 37.1772 46.0427 37.1772 45.3407C37.1772 44.8259 37.2147 44.5826 37.2989 44.5639C37.3551 44.5451 37.6827 44.4796 38.0196 44.4235C39.9009 44.0678 41.5108 42.5702 41.9975 40.7077C42.5871 38.4426 41.3236 36.1027 39.096 35.3352C38.7965 35.2322 38.291 35.1012 37.9634 35.045L37.3644 34.9421V31.8159V28.6898H37.5797C37.8792 28.6898 38.6654 28.9144 39.0117 29.0923C39.8167 29.5134 40.3127 30.2435 40.4157 31.1888C40.4999 31.9376 40.6497 32.1529 41.0989 32.1529C41.595 32.1529 41.7635 31.9376 41.7635 31.3011C41.7541 29.345 40.4438 27.8755 38.2817 27.4356C37.8886 27.3513 37.5329 27.2858 37.5048 27.2858C37.4767 27.2858 37.458 26.874 37.458 26.3686C37.458 25.2641 37.3551 25.0395 36.8122 25.0395C36.5782 25.0395 36.4004 25.105 36.2974 25.2173ZM36.0541 30.9642C36.0541 32.2184 36.026 33.5756 35.9885 33.9687L35.9324 34.6894L35.6422 34.6238C34.1166 34.2963 33.1432 33.248 33.1245 31.9189C33.1058 31.011 33.3585 30.4307 34.0792 29.71C34.5191 29.2701 34.8467 29.0361 35.2117 28.8957C36.1196 28.5494 36.0541 28.3716 36.0541 30.9642ZM38.7122 36.6455C39.4048 36.9076 40.1817 37.619 40.5186 38.3116C40.8556 39.0042 40.8743 39.9682 40.5561 40.7825C40.1068 41.9338 38.9088 42.8791 37.5797 43.1318L37.2708 43.188V39.8372C37.2708 37.9933 37.2989 36.4583 37.3457 36.4209C37.4487 36.3086 38.1132 36.4209 38.7122 36.6455Z"
            fill="#002334"
          ></path>
        </svg>
      ),
      text: (
        <>
          {t('ui-core.usp.0.text0', { defaultValue: 'Try Risk-Free' })}
          <br className="block lg:hidden" />{' '}
          {t('ui-core.usp.0.text1', { defaultValue: 'For 60 Days' })}
        </>
      ),
    },
    {
      icon: (
        //@ts-expect-error - Imgix type error because of JSX runtime
        <Imgix
          src="https://cdn.shopify.com/s/files/1/0612/5086/3345/files/cs23-icon.png?v=1717611673"
          htmlAttributes={{ alt: 'DIY & Pro Install Options' }}
          className="w-cove-50 h-cove-50 lg:w-cove-75 lg:h-cove-75"
        />
      ),
      text: (
        <>
          {t('ui-core.usp.1.text0', { defaultValue: 'DIY & Pro' })}
          <br className="block lg:hidden" />{' '}
          {t('ui-core.usp.1.text1', { defaultValue: 'Install Options' })}
        </>
      ),
    },
    {
      icon: (
        <svg
          fill="none"
          viewBox="0 0 73 73"
          xmlns="http://www.w3.org/2000/svg"
          className="w-cove-50 h-cove-50 lg:w-cove-75 lg:h-cove-75"
        >
          <circle cx="36.4277" cy="36.4277" fill="#F9F9F9" r="36.4277"></circle>
          <path
            d="M50.6061 31.3874H22V51.5071H50.6061V31.3874Z"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
          <path
            d="M50.6061 31.3874L36.3098 22L22 31.3874"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
          <path
            d="M33.5387 38.0984H26.8276V51.5205H33.5387V38.0984Z"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
          <path
            d="M45.7772 38.0984H37.8691V44.8095H45.7772V38.0984Z"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
          <path
            d="M41.8232 25.6178V22H46.2749V28.3883"
            stroke="#002334"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6446"
          ></path>
        </svg>
      ),
      text: (
        <>
          {t('ui-core.usp.2.text0', { defaultValue: '24/7' })}
          <br className="block lg:hidden" />{' '}
          {t('ui-core.usp.2.text1', { defaultValue: 'Support' })}
        </>
      ),
    },
  ];

  const variantItems = {
    base: items,
    compact: compactItems,
  };

  return (
    <section {...props}>
      <div className={uspWrapperVariants({ variant })}>
        {variantItems[variant].map(({ icon, text }, index) => {
          return (
            <div
              key={`usp-icon-${index}`}
              className={uspContainerVariants({ variant })}
            >
              <div className={uspIconVariants({ variant })}>{icon}</div>

              <div className={uspTextVariants({ variant })}>{text}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default USPIcons;
